import { useCallback, useEffect, useState, useRef } from 'react'
import {
    Upload,
    FileSpreadsheet,
    X,
    Check,
    Download,
    Loader2,
    BarChart3,
    AlertTriangle,
    Activity,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { uploadFile } from '@/lib/admin.api'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { VisibilityKeywordsCitiesSettings } from '@/components/workspace/VisibilityKeywordsCitiesSettings'
import { Switch } from '@/components/ui/switch'
import { trpc } from '@/lib/trpc'

// Excel Upload Component for Data Ingestion
function ExcelUploadSection({
    workspaceId,
    onUploadSuccess,
}: {
    workspaceId?: string
    onUploadSuccess?: () => void
}) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle')
    const [uploadProgress, setUploadProgress] = useState(0)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [uploadResult, setUploadResult] = useState<{ rowsProcessed: number; skusAdded: number } | null>(null)

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
        
        const files = e.dataTransfer.files
        if (files.length > 0) {
            handleFileSelect(files[0])
        }
    }

    const handleFileSelect = (file: File) => {
        // Validate file type
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            'application/vnd.ms-excel', // .xls
            'text/csv', // .csv
        ]
        const validExtensions = ['.xlsx', '.xls', '.csv']
        
        const hasValidExtension = validExtensions.some(ext => 
            file.name.toLowerCase().endsWith(ext)
        )
        
        if (!validTypes.includes(file.type) && !hasValidExtension) {
            setErrorMessage('Please upload an Excel file (.xlsx, .xls) or CSV file (.csv)')
            return
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setErrorMessage('File size must be less than 10MB')
            return
        }

        setSelectedFile(file)
        setErrorMessage(null)
        setUploadResult(null)
    }

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files && files.length > 0) {
            handleFileSelect(files[0])
        }
    }

    const handleUpload = async () => {
        if (!selectedFile) return
        if (!workspaceId) {
            setErrorMessage('Please select a workspace before uploading.')
            return
        }

        setUploadState('uploading')
        setUploadProgress(0)
        setErrorMessage(null)

        try {
            // Simulate upload progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval)
                        return prev
                    }
                    return prev + 10
                })
            }, 200)

            // Upload file via API
            const result = await uploadFile(selectedFile, 'availability', workspaceId)
            
            clearInterval(progressInterval)
            setUploadProgress(100)
            
            // Set the actual result from API
            setUploadResult({
                rowsProcessed: result.totalRows,
                skusAdded: result.insertedRows,
            })
            
            setUploadState('success')
            onUploadSuccess?.()
        } catch (error) {
            setUploadState('error')
            setErrorMessage(error instanceof Error ? error.message : 'Upload failed. Please try again.')
        }
    }

    const handleReset = () => {
        setSelectedFile(null)
        setUploadState('idle')
        setUploadProgress(0)
        setErrorMessage(null)
        setUploadResult(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const downloadTemplate = () => {
        // Create a sample CSV template
        const templateContent = `SKU,Product Name,Brand,Category,City,Store Name,OSA %,DOI Days,Stock Status,Issue Type
BLINK-001,Organic Quinoa 500g,Nourish You,Health Foods,Mumbai,Store A,95,5,In Stock,
BLINK-002,Brown Rice 1kg,Nourish You,Grains,Delhi,Store B,80,3,In Stock,
BLINK-003,Chia Seeds 250g,Nourish You,Health Foods,Bangalore,Store C,0,0,Out of Stock,Suppressed`
        
        const blob = new Blob([templateContent], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'availability_template.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Upload className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">Upload Availability Data</CardTitle>
                            <CardDescription>
                                Import your availability data from an Excel or CSV file
                            </CardDescription>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                        <Download className="h-4 w-4 mr-2" />
                        Download Template
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {/* Upload Area */}
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                        "relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                        isDragging 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/50 hover:bg-muted/30",
                        uploadState === 'success' && "border-green-500 bg-green-500/5",
                        uploadState === 'error' && "border-destructive bg-destructive/5"
                    )}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileInputChange}
                        className="hidden"
                    />

                    {uploadState === 'idle' && !selectedFile && (
                        <>
                            <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-foreground font-medium mb-1">
                                Drop your file here or click to browse
                            </p>
                            <p className="text-sm text-muted-foreground">
                                Supports .xlsx, .xls, and .csv files (max 10MB)
                            </p>
                        </>
                    )}

                    {selectedFile && uploadState === 'idle' && (
                        <div className="flex items-center justify-center gap-4">
                            <div className="flex items-center gap-3">
                                <FileSpreadsheet className="h-10 w-10 text-primary" />
                                <div className="text-left">
                                    <p className="font-medium text-foreground">{selectedFile.name}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {(selectedFile.size / 1024).toFixed(1)} KB
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleReset()
                                }}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    )}

                    {uploadState === 'uploading' && (
                        <div className="space-y-4">
                            <Loader2 className="h-10 w-10 text-primary mx-auto animate-spin" />
                            <p className="font-medium text-foreground">Uploading and processing...</p>
                            <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-2 overflow-hidden">
                                <div 
                                    className="h-full bg-primary transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <p className="text-sm text-muted-foreground">{uploadProgress}% complete</p>
                        </div>
                    )}

                    {uploadState === 'success' && uploadResult && (
                        <div className="space-y-4">
                            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                                <Check className="h-6 w-6 text-green-500" />
                            </div>
                            <p className="font-medium text-foreground">Upload Successful!</p>
                            <div className="flex items-center justify-center gap-6 text-sm">
                                <div>
                                    <span className="text-muted-foreground">Rows Processed:</span>{' '}
                                    <span className="font-semibold text-foreground">{uploadResult.rowsProcessed}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">SKUs Added:</span>{' '}
                                    <span className="font-semibold text-foreground">{uploadResult.skusAdded}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {uploadState === 'error' && (
                        <div className="space-y-4">
                            <div className="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center mx-auto">
                                <X className="h-6 w-6 text-destructive" />
                            </div>
                            <p className="font-medium text-foreground">Upload Failed</p>
                            <p className="text-sm text-destructive">{errorMessage}</p>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {errorMessage && uploadState === 'idle' && (
                    <p className="mt-3 text-sm text-destructive">{errorMessage}</p>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-4">
                    {(uploadState === 'success' || uploadState === 'error') && (
                        <Button variant="outline" onClick={handleReset}>
                            Upload Another File
                        </Button>
                    )}
                    {selectedFile && uploadState === 'idle' && (
                        <Button onClick={handleUpload}>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload File
                        </Button>
                    )}
                </div>

                {/* Expected Format Info */}
                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-sm text-foreground mb-2">Expected File Format</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                        Your file should contain the following columns:
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {['SKU', 'Product Name', 'Brand', 'Category', 'City', 'Store Name', 'OSA %', 'DOI Days', 'Stock Status', 'Issue Type'].map((col) => (
                            <span key={col} className="px-2 py-1 text-xs font-medium bg-card border border-border rounded">
                                {col}
                            </span>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export function AvailabilitySettingsPage() {
    const { currentWorkspace } = useWorkspace()
    const workspaceId = currentWorkspace?.id
    const [isEnabled, setIsEnabled] = useState(true)
    const [isSettingsLoading, setIsSettingsLoading] = useState(true)
    const [isSavingSettings, setIsSavingSettings] = useState(false)
    const [settingsSaveStatus, setSettingsSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
    const [settingsError, setSettingsError] = useState<string | null>(null)

    const utils = trpc.useUtils()
    const updateSettingsMutation = trpc.availability.updateSettings.useMutation()

    useEffect(() => {
        if (!workspaceId) {
            setIsSettingsLoading(false)
            return
        }

        const loadSettings = async () => {
            try {
                setIsSettingsLoading(true)
                setSettingsError(null)
                const settings = await utils.availability.settings.fetch({ workspaceId })
                if (settings) {
                    setIsEnabled(settings.isEnabled)
                }
            } catch (error) {
                console.error('Failed to load availability settings:', error)
                setSettingsError('Failed to load availability settings')
            } finally {
                setIsSettingsLoading(false)
            }
        }

        loadSettings()
    }, [workspaceId])

    const loadAvailabilityCityIds = useCallback(async () => {
        if (!workspaceId) {
            return null
        }
        const settings = await utils.availability.settings.fetch({ workspaceId })
        return settings?.enabledCityIds ?? null
    }, [workspaceId])

    const saveAvailabilityCityIds = useCallback(
        async (enabledCityIds: number[] | null) => {
            if (!workspaceId) {
                return
            }
            await updateSettingsMutation.mutateAsync({ workspaceId, enabledCityIds })
        },
        [workspaceId]
    )

    const handleToggleAvailability = async (nextEnabled: boolean) => {
        if (!workspaceId) return

        const previousEnabled = isEnabled
        setIsEnabled(nextEnabled)

        try {
            setIsSavingSettings(true)
            setSettingsError(null)
            await updateSettingsMutation.mutateAsync({ workspaceId, isEnabled: nextEnabled })
            setSettingsSaveStatus('success')
            setTimeout(() => setSettingsSaveStatus('idle'), 3000)
        } catch (error) {
            console.error('Failed to update availability settings:', error)
            setIsEnabled(previousEnabled)
            setSettingsError(error instanceof Error ? error.message : 'Failed to save availability settings')
            setSettingsSaveStatus('error')
            setTimeout(() => setSettingsSaveStatus('idle'), 3000)
        } finally {
            setIsSavingSettings(false)
        }
    }

    return (
        <div className="p-6 md:p-8 w-full animate-fade-in">
            <div className="max-w-[1000px] mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <BarChart3 className="h-8 w-8 text-primary" />
                        <h1 className="app-page-title">Availability Settings</h1>
                    </div>
                    <p className="app-page-subtitle">
                        Upload and manage your availability data for the Availability module.
                    </p>
                </div>

                {/* Info Card */}
                <Card className="mb-6 border-primary/20 bg-primary/5">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-primary mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-foreground">
                                    Data Ingestion for Availability Module
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Upload your inventory and availability data here. The data will be processed and displayed in the Availability module, 
                                    allowing you to track OSA (On-Shelf Availability), DOI (Days of Inventory), and listing issues across cities and stores.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Activity className="h-5 w-5 text-primary" />
                                <div>
                                    <CardTitle className="text-lg">Availability Scraping</CardTitle>
                                    <CardDescription>
                                        Enable or disable automated availability scraping for this workspace.
                                    </CardDescription>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span
                                    className={`text-sm font-medium ${isEnabled ? 'text-green-600' : 'text-muted-foreground'}`}
                                >
                                    {isEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <Switch
                                    checked={isEnabled}
                                    onCheckedChange={handleToggleAvailability}
                                    disabled={isSettingsLoading || isSavingSettings}
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {isSavingSettings && <p className="text-sm text-muted-foreground">Saving...</p>}
                        {settingsSaveStatus === 'success' && !isSavingSettings && (
                            <p className="text-sm text-foreground flex items-center gap-2">
                                <Check className="h-4 w-4" />
                                Saved
                            </p>
                        )}
                        {settingsSaveStatus === 'error' && (
                            <p className="mt-3 text-sm text-destructive">
                                Failed to save availability settings.
                            </p>
                        )}
                    </CardContent>
                </Card>

                {settingsError && (
                    <div className="mb-6 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                        {settingsError}
                    </div>
                )}

                {/* Cities Selection */}
                {workspaceId && (
                    <div className="mb-6">
                        <VisibilityKeywordsCitiesSettings
                            workspaceId={workspaceId}
                            showKeywords={false}
                            loadCityIds={loadAvailabilityCityIds}
                            saveCityIds={saveAvailabilityCityIds}
                            citySectionTitle="Enabled Cities"
                            citySectionDescription="Select which cities should be included in availability scraping."
                            citySaveButtonLabel="Save Cities"
                        />
                    </div>
                )}

                {/* Excel Upload Section */}
                <ExcelUploadSection workspaceId={workspaceId} />
            </div>
        </div>
    )
}
